import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["balance", "rate", "payment", "error", "errorMessage",
                    "results", "months", "totalInterest", "totalPaid", "payoffDate"]

  // URL-fragment key → target name. Lets [CALCULATOR_EXAMPLE:payoff balance=... apr=...
  // monthly=...] shortcodes in articles deep-link a reader into a pre-filled
  // calculator scenario.
  URL_PARAM_TARGETS = {
    balance: "balance",
    apr:     "rate",
    monthly: "payment"
  }

  connect() {
    this._applyUrlParams()
    this.calculate()
  }

  _applyUrlParams() {
    const params = new URLSearchParams(window.location.hash.slice(1))
    for (const [key, target] of Object.entries(this.URL_PARAM_TARGETS)) {
      const val = params.get(key)
      if (val == null) continue
      const hasFn = `has${target.charAt(0).toUpperCase()}${target.slice(1)}Target`
      if (this[hasFn]) this[`${target}Target`].value = val
    }
  }

  calculate() {
    const balance  = parseFloat(this.balanceTarget.value.replace(/,/g, "")) || 0
    const apr      = parseFloat(this.rateTarget.value) || 0
    const payment  = parseFloat(this.paymentTarget.value.replace(/,/g, "")) || 0

    if (balance <= 0 || apr <= 0 || payment <= 0) {
      this.errorTarget.classList.add("hidden")
      this.resultsTarget.classList.add("hidden")
      return
    }

    const monthlyRate = apr / 100 / 12
    const interest    = balance * monthlyRate

    if (payment <= interest) {
      this.errorMessageTarget.innerHTML =
        `Your monthly payment of $${payment.toLocaleString()} doesn't cover the monthly interest of $${interest.toFixed(2)}. ` +
        `You need at least <strong>$${Math.ceil(interest + 1).toLocaleString()}/mo</strong> to make progress.`
      this.errorTarget.classList.remove("hidden")
      this.resultsTarget.classList.add("hidden")
      return
    }

    this.errorTarget.classList.add("hidden")

    let remaining     = balance
    let months        = 0
    let totalInterest = 0

    while (remaining > 0.01 && months < 600) {
      const intCharge  = remaining * monthlyRate
      const prinCharge = Math.min(payment - intCharge, remaining)
      totalInterest   += intCharge
      remaining       -= prinCharge
      months++
    }

    const payoffDate = new Date()
    payoffDate.setMonth(payoffDate.getMonth() + months)
    const dateStr = payoffDate.toLocaleDateString("en-US", { month: "long", year: "numeric" })

    this.monthsTarget.textContent        = months < 12
      ? `${months} month${months !== 1 ? "s" : ""}`
      : `${Math.floor(months / 12)} yr ${months % 12} mo`
    this.totalInterestTarget.textContent = "$" + Math.round(totalInterest).toLocaleString()
    this.totalPaidTarget.textContent     = "$" + Math.round(balance + totalInterest).toLocaleString()
    this.payoffDateTarget.textContent    = dateStr
    this.resultsTarget.classList.remove("hidden")
  }
}
